import React, { useContext, useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import VisibilitySensor from "react-visibility-sensor";

import Page from "../../components/Page/Page";
import Loader from "../../components/Loader/Loader";
import Slider from "../../components/Slider/Slider";
import Ribbon from "../../components/Ribbon/Ribbon";
import Grid from "../../components/Grid/Grid";
import Button from "../../components/Button/Button";
import PdfReader from "../../components/PdfReader/PdfReader";
import NoResults from "../../components/NoResults/NoResults";
import StickyBrowseSection from "./StickyBrowseSection/StickyBrowseSection";
import FeaturedContent from "../__Common/FeaturedContent/FeaturedContent";
import Table from "./Table/Table";

import useBrowseFilters from "./useBrowseFilters";
import useContentRibbons from "../../core/apiHooks/useContentRibbons";
import useFeaturedPublications from "../../core/apiHooks/useFeaturedPublications";
import { useDocumentsWithFilters } from "../../core/apiHooks/useDocumentsWithFilters";

import { GlobalContext } from "../../GlobalContext";
import Routes from "../../core/Routes";
import { getSlidesPerView } from "../../core/formatters";
import { ContentRibbon } from "../../core/models/ContentRibbon";
import { iTag } from "../../core/models/Tag";
import { DocumentTypesEnum } from "../../core/enums/PublicationTypesEnum";
import { SORT_ENUM } from "../../components/Filters/Filters.helpers";
import { IconArrowRight, IconInfo } from "../../icons";

import "./Library.styles.scss";

const Library: React.FC = () => {
    /* -------------------------------------------------
     * Routes & global context
     * -------------------------------------------------*/
    const location = useLocation();
    const history = useHistory();
    const route = Routes.find(({ path }) => path === location.pathname) || Routes[0];
    const { inMyLibrary, isSearchOpened, wWidth, getRem } = useContext(GlobalContext);

    /* -------------------------------------------------
     * UI state
     * -------------------------------------------------*/
    const [isMacInfoPopupOpened, setIsMacInfoPopupOpened] = useState(false);
    const [isViewChanging, setIsViewChanging] = useState(false);
    const [viewMode, setViewMode] = useState<"tile" | "table">(
        () => (localStorage.getItem("browseSection:viewMode") as "tile" | "table") || "tile"
    );
    const handleViewModeChange = (mode: "tile" | "table") => {
        if (mode !== viewMode) {
            setIsViewChanging(true);
            setViewMode(mode);
            setTimeout(() => setIsViewChanging(false), 300);
        }
    };

    /* -------------------------------------------------
     * Data fetching
     * -------------------------------------------------*/
    const { data: ribbons = [], isLoading: isRibbonsLoading } = useContentRibbons(inMyLibrary);
    const { data: featuredPublications, isLoading: isFeaturedLoading } = useFeaturedPublications();

    /* -------------------------------------------------
     * Filters – handled by custom hook
     * -------------------------------------------------*/
    const { browseFilters, updateBrowseFilters, isNewRelease, setIsNewRelease, macRibbonId } = useBrowseFilters({
        inMyLibrary,
        isSearchOpened,
        location,
        history,
        ribbons
    });

    /* -------------------------------------------------
     * Documents for grid / table
     * -------------------------------------------------*/
    const tilesPerView = getSlidesPerView(wWidth - getRem(2 * 48), 210);
    const take = 5 * tilesPerView;

    const {
        isLoading: isGridLoading,
        data: gridDocs = [],
        hasNextItems,
        incSkip
    } = useDocumentsWithFilters(
        {
            ribbonsIds: browseFilters.ribbonsIds.filter((id) => id !== 18),
            categoriesIds: browseFilters.categoriesIds,
            subscriptionPackageIds: browseFilters.subscriptionPackageIds,
            tagsIds: browseFilters.tagsIds,
            orderBy: browseFilters.orderBy,
            sortOrder: browseFilters.sortOrder,
            hasAccess: browseFilters.hasAccess,
            types: browseFilters.types,
            searchText: browseFilters.searchQuery
        },
        take
    );

    /* -------------------------------------------------
     * Show short loader only when switching TO Publication type (tile & table)
     * -------------------------------------------------*/
    const prevTypesRef = useRef<DocumentTypesEnum[]>(browseFilters.types);

    useEffect(() => {
        const prev = prevTypesRef.current;
        const nowPublication = browseFilters.types.length === 1 && browseFilters.types[0] === DocumentTypesEnum.Publication;
        const wasPublication = prev.length === 1 && prev[0] === DocumentTypesEnum.Publication;
        if (nowPublication && !wasPublication) {
            setIsViewChanging(true);
            const id = setTimeout(() => setIsViewChanging(false), 400);
            prevTypesRef.current = browseFilters.types;
            return () => clearTimeout(id);
        }
        prevTypesRef.current = browseFilters.types;
    }, [browseFilters.types]);

    /* -------------------------------------------------
     * Helpers
     * -------------------------------------------------*/
    const hasTypeFilter =
        !(browseFilters.types.length === 0) &&
        !(browseFilters.types.length === 1 && browseFilters.types[0] === DocumentTypesEnum.Publication);

    const shouldShowFilteredResults =
        viewMode === "table" ||
        browseFilters.ribbonsIds.length > 0 ||
        browseFilters.tagsIds.length > 0 ||
        browseFilters.subscriptionPackageIds.length > 0 ||
        isNewRelease ||
        hasTypeFilter;

    const handleTagClick = (tag: iTag) => {
        updateBrowseFilters({ tagsIds: [tag.id], subscriptionPackageIds: [] });
    };

    /* -------------------------------------------------
     * Render
     * -------------------------------------------------*/
    return (
        <Page title={route?.title || ""} isShownStickyFooter={!!ribbons.length}>
            <div className="Library">
                {/* Featured content */}
                {!isFeaturedLoading && featuredPublications.length > 0 && (
                    <div className="Library__section">
                        <Slider>
                            {featuredPublications.map((p) => (
                                <FeaturedContent key={p.id} data={p} />
                            ))}
                        </Slider>
                    </div>
                )}
                {isFeaturedLoading && (
                    <div className="Library__section">
                        <Loader />
                    </div>
                )}

                {/* Browse & Results */}
                <div className="Library__section">
                    {isRibbonsLoading && !isFeaturedLoading && <Loader />}

                    {!isRibbonsLoading && (
                        <>
                            {/* Filters */}
                            <div className="Library__browse-wrapper">
                                <StickyBrowseSection
                                    viewMode={viewMode}
                                    onViewModeChange={handleViewModeChange}
                                    filters={browseFilters}
                                    onChange={(filters) => updateBrowseFilters(filters as any)}
                                />
                            </div>

                            {/* Results */}
                            {shouldShowFilteredResults ? (
                                <div className="Library__results">
                                    {isViewChanging && (
                                        <div className="Library__view-loader">
                                            <Loader />
                                        </div>
                                    )}

                                    {/* Tile view */}
                                    {!isViewChanging && viewMode === "tile" ? (
                                        <>
                                            {isGridLoading ? (
                                                <div className="Library__tile-loader">
                                                    <Loader />
                                                </div>
                                            ) : (
                                                <>
                                                    <Grid
                                                        data={new ContentRibbon({ clientModel: { items: gridDocs } })}
                                                        columnsNumber={tilesPerView}
                                                    />
                                                    {!gridDocs.length && <NoResults title="No Results found" subTitle="" />}
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        /* Table view */
                                        !isViewChanging && (
                                            <div
                                                className={`Library__table-container ${!gridDocs.length ? "has-no-results" : ""
                                                    } ${isGridLoading ? "is-loading" : ""}`}
                                            >
                                                <Table
                                                    data={gridDocs}
                                                    onRowClick={(publication) => {
                                                        if (publication.customLink && publication.hasAccess) {
                                                            if (/^https?:\/\//.test(publication.customLink)) {
                                                                window.open(publication.customLink, "_blank");
                                                            } else {
                                                                history.push(publication.customLink);
                                                            }
                                                        } else {
                                                            history.push(`/details/${publication.id}`);
                                                        }
                                                    }}
                                                    onTagClick={
                                                        (browseFilters.types.length === 1 &&
                                                            browseFilters.types[0] === DocumentTypesEnum.Mac) ||
                                                            (macRibbonId ? browseFilters.ribbonsIds.includes(macRibbonId) : false)
                                                            ? undefined
                                                            : handleTagClick
                                                    }
                                                    isLoading={isGridLoading}
                                                    searchQuery={browseFilters.searchQuery}
                                                    orderBy={browseFilters.orderBy}
                                                    sortOrder={browseFilters.sortOrder}
                                                    onSort={(orderBy, sortOrder) => updateBrowseFilters({ orderBy, sortOrder })}
                                                    typeFilter={browseFilters.types}
                                                    onTypeFilterChange={(types) => {
                                                        if (!(types.length === 1 && types[0] === DocumentTypesEnum.Publication)) {
                                                            // MAC and Article already show API loader
                                                        }
                                                        if (types.length === 1 && types[0] === DocumentTypesEnum.Mac) {
                                                            updateBrowseFilters({
                                                                types: types as DocumentTypesEnum[],
                                                                tagsIds: [],
                                                                subscriptionPackageIds: [],
                                                                orderBy: "updateDate"
                                                            });
                                                        } else {
                                                            updateBrowseFilters({
                                                                types: types as DocumentTypesEnum[],
                                                                orderBy: SORT_ENUM.DATE
                                                            });
                                                        }
                                                    }}
                                                />
                                            </div>
                                        )
                                    )}

                                    {/* Infinite scroll loader */}
                                    {viewMode === "tile" && isGridLoading && !isViewChanging && (
                                        <div className={`Browse__loader ${gridDocs.length === 0 ? "is-initial" : ""}`}>
                                            <Loader />
                                        </div>
                                    )}

                                    {/* Infinite scroll sentinel */}
                                    <VisibilitySensor
                                        onChange={(visible) => {
                                            if (visible && hasNextItems && !isGridLoading) incSkip();
                                        }}
                                        partialVisibility
                                    >
                                        {!isGridLoading ? <div className="Browse__sensor" /> : <div />}
                                    </VisibilitySensor>
                                </div>
                            ) : (
                                /* Ribbons */
                                <>
                                    {isViewChanging && (
                                        <div className="Library__results">
                                            <div className="Library__view-loader">
                                                <Loader />
                                            </div>
                                        </div>
                                    )}
                                    {!isViewChanging &&
                                        ribbons.map((ribbon) => {
                                            const ribbonTitle = (
                                                <>
                                                    {ribbon.title}
                                                    {ribbon.isMacRibbon && (
                                                        <>
                                                            <Button
                                                                className="Library__title-icon"
                                                                onClick={() => setIsMacInfoPopupOpened(true)}
                                                            >
                                                                <IconInfo />
                                                            </Button>
                                                            <PdfReader
                                                                isOpened={isMacInfoPopupOpened}
                                                                close={() => setIsMacInfoPopupOpened(false)}
                                                                title="MAC Diagram"
                                                                fileUrl={"/MAC_DIAGRAM.pdf"}
                                                            />
                                                        </>
                                                    )}
                                                    <Button
                                                        className="ShowAllButton"
                                                        onClick={() => {
                                                            setIsNewRelease(false);
                                                            updateBrowseFilters({ ...browseFilters, ribbonsIds: [ribbon.id] });
                                                        }}
                                                    >
                                                        Show all
                                                        <IconArrowRight style={{ transform: "scale(0.8) translate(-30%, 3%)" }} />
                                                    </Button>
                                                </>
                                            );
                                            return <Ribbon key={ribbon.id} data={{ ...ribbon, title: ribbonTitle }} />;
                                        })}
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </Page>
    );
};

export default Library;
